import { useDispatch } from "react-redux";

import { useMedplum } from "@medplum/react";

import { Button, Drawer, Group, useDrawersStack } from "@mantine/core";

import {
  LABOR_QUESTIONNAIRE_DISPLAY_NAMES,
  QuestionnaireTitle,
  QuestionnaireType,
} from "@/encounters/components/EncounterLabor/constants/labor-questionnaires";
import { EncounterLaborQuickButtonDrawer } from "@/encounters/components/EncounterLabor/EncounterLaborDrawer/EncounterLaborQuickButtonDrawer";
import {
  getNativeLaborFlowsheetQuickAddDrawer,
  isNativeLaborFlowsheetDrawerQuestionnaire,
} from "@/encounters/components/EncounterLabor/flowsheet-forms/registry";
import { useBirthObservations } from "@/encounters/components/EncounterLabor/hooks/useBirthObservations";
import { fetchLaborStageObservations } from "@/encounters/components/EncounterLabor/services/labor";
import { dedupeFinalLaborStageObservations } from "@/encounters/components/EncounterLabor/utils/labor-stage-dedupe";
import { loadLaborStages } from "@/store/slices/labor-stages";

const FLOW_SHEET_BUTTONS = [
  {
    title: QuestionnaireTitle.FHR,
    questionnaireName: QuestionnaireType.FetalHeartRate,
    displayTitle: LABOR_QUESTIONNAIRE_DISPLAY_NAMES[QuestionnaireType.FetalHeartRate],
  },
  {
    title: QuestionnaireTitle.LaborVitals,
    questionnaireName: QuestionnaireType.LaborVitals,
    displayTitle: LABOR_QUESTIONNAIRE_DISPLAY_NAMES[QuestionnaireType.LaborVitals],
  },
  {
    title: QuestionnaireTitle.Contractions,
    questionnaireName: QuestionnaireType.Contractions,
    displayTitle: LABOR_QUESTIONNAIRE_DISPLAY_NAMES[QuestionnaireType.Contractions],
  },
  {
    title: QuestionnaireTitle.CervicalExam,
    questionnaireName: QuestionnaireType.CervicalExam,
    displayTitle: LABOR_QUESTIONNAIRE_DISPLAY_NAMES[QuestionnaireType.CervicalExam],
  },
  {
    title: QuestionnaireTitle.ROM,
    questionnaireName: QuestionnaireType.RuptureOfMembranes,
    displayTitle: LABOR_QUESTIONNAIRE_DISPLAY_NAMES[QuestionnaireType.RuptureOfMembranes],
  },
  {
    title: QuestionnaireTitle.Position,
    questionnaireName: QuestionnaireType.Position,
    displayTitle: LABOR_QUESTIONNAIRE_DISPLAY_NAMES[QuestionnaireType.Position],
  },
  {
    title: QuestionnaireTitle.Note,
    questionnaireName: QuestionnaireType.AssessmentNote,
    displayTitle: LABOR_QUESTIONNAIRE_DISPLAY_NAMES[QuestionnaireType.AssessmentNote],
  },
  {
    title: QuestionnaireTitle.Birthing,
    questionnaireName: QuestionnaireType.Birthing,
    displayTitle: LABOR_QUESTIONNAIRE_DISPLAY_NAMES[QuestionnaireType.Birthing],
  },
] as const;

type FlowSheet = (typeof FLOW_SHEET_BUTTONS)[number];
type QuestionnaireName = FlowSheet["questionnaireName"];

export interface EncounterLaborQuickButtonsProps {
  encounterId: string;
  clientId: string;
  editMode?: boolean;
}

export function EncounterLaborQuickButtons({
  encounterId,
  clientId,
  editMode = false,
}: EncounterLaborQuickButtonsProps) {
  const { setOptimisticBirth, clearOptimisticBirth, birthDateTime } = useBirthObservations(
    encounterId,
    clientId,
  );

  // Filter out Position button if birth has occurred
  const visibleButtons = birthDateTime
    ? FLOW_SHEET_BUTTONS.filter((button) => button.questionnaireName !== QuestionnaireType.Position)
    : FLOW_SHEET_BUTTONS;

  // Get all questionnaire names from visible buttons
  const questionnaireNames = visibleButtons.map((button) => button.questionnaireName);

  // Use the useDrawersStack hook to manage multiple drawers
  const stack = useDrawersStack(questionnaireNames);
  const medplum = useMedplum();
  const dispatch = useDispatch();

  const handleRefresh = async () => {
    try {
      const observations = await fetchLaborStageObservations(medplum, clientId, encounterId);

      dispatch(loadLaborStages(dedupeFinalLaborStageObservations(observations)));
    } catch (error) {
      console.error("Error refreshing labor stages:", error);
    }
  };

  const handleClose = (questionnaireName: QuestionnaireName) => {
    stack.close(questionnaireName);
  };

  return (
    <>
      <Group gap="sm" data-testid="labor-quick-buttons">
        {visibleButtons.map((flow) => (
          <Button
            size="xs"
            key={flow.title}
            color="burgundy"
            variant="outline"
            data-testid={`labor-quick-button-${flow.title}`}
            onClick={() => {
              stack.open(flow.questionnaireName);
            }}
          >
            {flow.title}
          </Button>
        ))}
      </Group>

      <Drawer.Stack>
        {visibleButtons.map((flow) => {
          const drawerRegistration = stack.register(flow.questionnaireName);
          const handleDrawerClose = () => handleClose(flow.questionnaireName);

          if (isNativeLaborFlowsheetDrawerQuestionnaire(flow.questionnaireName)) {
            const NativeDrawer = getNativeLaborFlowsheetQuickAddDrawer(flow.questionnaireName);
            if (NativeDrawer) {
              return (
                <NativeDrawer
                  mode="quick-add"
                  key={flow.questionnaireName}
                  {...drawerRegistration}
                  clientId={clientId}
                  encounterId={encounterId}
                  onRefresh={handleRefresh}
                  title={flow.displayTitle}
                  onClose={handleDrawerClose}
                />
              );
            }
          }

          return (
            <EncounterLaborQuickButtonDrawer
              key={flow.questionnaireName}
              {...drawerRegistration}
              clientId={clientId}
              encounterId={encounterId}
              onRefresh={handleRefresh}
              onClose={handleDrawerClose}
              onBirthCreate={setOptimisticBirth}
              questionnaireTitle={flow.displayTitle}
              questionnaireName={flow.questionnaireName}
              onClearOptimisticBirthDateTime={clearOptimisticBirth}
            />
          );
        })}
      </Drawer.Stack>
    </>
  );
}
